import {
    ALL_SENTIMENT_LABEL,
    NEUTRAL_SENTIMENT_LABEL,
    NEGATIVE_SENTIMENT_LABEL,
    POSITIVE_SENTIMENT_LABEL
} from '@/assets/common/sentiments';
import numberFormatter from '@/helpers/numberFormatter';
import calcPercentages from '@/helpers/calcPercentages';

export const IMPACT_SCORE_LABEL = 'Impact score';
export const ENGAGEMENTS_LABEL = 'Engagements';
export const SHARES_LABEL = 'Shares';
export const REACH_LABEL = 'Reach';

export const SERIES_DISPLAY_LABELS = {
    [ENGAGEMENTS_LABEL]: 'All Engagements'
};

export const seriesDisplayLabel = label => SERIES_DISPLAY_LABELS[label] || label;

export const SERIES_GROUPS = [
    {
        title: 'Sentiment',
        labels: [IMPACT_SCORE_LABEL, POSITIVE_SENTIMENT_LABEL, NEGATIVE_SENTIMENT_LABEL, NEUTRAL_SENTIMENT_LABEL]
    },
    {
        title: 'Volume',
        labels: [ALL_SENTIMENT_LABEL, ENGAGEMENTS_LABEL, SHARES_LABEL, REACH_LABEL]
    }
];

export const SERIES_LABELS = SERIES_GROUPS.flatMap(group => group.labels);

export const DEFAULT_DISABLED_DATASETS = [NEUTRAL_SENTIMENT_LABEL, SHARES_LABEL, REACH_LABEL];

export const buildSeriesVisibility = hiddenLabels => Object.fromEntries(
    SERIES_LABELS.map(label => [label, !hiddenLabels.includes(label)])
);

export const hiddenSeriesLabels = seriesVisibility => SERIES_LABELS.filter(label => !seriesVisibility[label]);

export const SERIES_COLORS = {
    [ALL_SENTIMENT_LABEL]: { border: 'rgba(178, 34, 215, 1)', background: 'rgba(178, 34, 215, 0.24)' },
    [POSITIVE_SENTIMENT_LABEL]: { border: 'rgba(95, 191, 127, 1)', background: 'rgba(95, 191, 127, 0.65)' },
    [NEGATIVE_SENTIMENT_LABEL]: { border: 'rgba(246, 82, 114, 1)', background: 'rgba(246, 82, 114, 0.65)' },
    [NEUTRAL_SENTIMENT_LABEL]: { border: 'rgba(160, 175, 189, 1)', background: 'rgba(194, 199, 204, 0.65)' },
    [ENGAGEMENTS_LABEL]: { border: 'rgb(149, 205, 203)', background: 'rgba(149, 205, 203, 0.55)' },
    [SHARES_LABEL]: { border: 'rgb(107, 122, 30)', background: 'rgba(107, 122, 30, 0.35)' },
    [REACH_LABEL]: { border: 'rgb(209, 150, 111)', background: 'rgba(209, 150, 111, 0.5)' },
    [IMPACT_SCORE_LABEL]: { border: '#4285F4', background: 'rgba(66, 133, 244, 0.35)' }
};

export const TRANSPARENT_COLOR = 'rgba(0, 0, 0, 0)';

const isNumber = value => typeof value === 'number' && !Number.isNaN(value);

export const formatSeriesTotal = (label, value) => {
    if (!isNumber(value)) {
        return '';
    }

    return (label === IMPACT_SCORE_LABEL) ? `${numberFormatter(value)}/10` : `${numberFormatter(value)}`;
};

const SENTIMENT_DESCRIPTIONS = {
    [POSITIVE_SENTIMENT_LABEL]: 'positive',
    [NEGATIVE_SENTIMENT_LABEL]: 'negative',
    [NEUTRAL_SENTIMENT_LABEL]: 'neutral'
};

const percentageOfAll = (value, total) => (
    (isNumber(value) && isNumber(total) && total > 0) ? `${calcPercentages(value, total).toFixed(2)}%` : null
);

/**
 * Info-only chip tooltip, copy trimmed from the metric card tooltips.
 * `totals` holds raw numbers by series label; `engagementsBreakdown` holds { likes, replies, shares }.
 */
export const seriesTooltip = (label, totals = {}, engagementsBreakdown = null) => {
    const title = seriesDisplayLabel(label);
    const value = formatSeriesTotal(label, totals[label]);
    const allDataPoints = totals[ALL_SENTIMENT_LABEL];

    if (label === ALL_SENTIMENT_LABEL) {
        const sentimentLabels = [POSITIVE_SENTIMENT_LABEL, NEGATIVE_SENTIMENT_LABEL, NEUTRAL_SENTIMENT_LABEL];

        return {
            title,
            value,
            description: 'Total collected mentions with valid metadata in the selected dataset.',
            breakdown: sentimentLabels
                .filter(sentimentLabel => isNumber(totals[sentimentLabel]))
                .map(sentimentLabel => ({
                    label: sentimentLabel,
                    value: formatSeriesTotal(sentimentLabel, totals[sentimentLabel]),
                    share: percentageOfAll(totals[sentimentLabel], allDataPoints)
                })),
            total: value
        };
    }

    if (label in SENTIMENT_DESCRIPTIONS) {
        const share = percentageOfAll(totals[label], allDataPoints);

        return {
            title,
            value,
            description: `Data points tagged as ${SENTIMENT_DESCRIPTIONS[label]}.${share ? ` ${share} of all data points.` : ''}`
        };
    }

    if (label === ENGAGEMENTS_LABEL) {
        return {
            title,
            value,
            description: 'Total engagements received by the data points.',
            breakdown: engagementsBreakdown
                ? [
                    { label: 'Likes', value: formatSeriesTotal(label, engagementsBreakdown.likes || 0) },
                    { label: 'Replies', value: formatSeriesTotal(label, engagementsBreakdown.replies || 0) },
                    { label: 'Shares / RT', value: formatSeriesTotal(label, engagementsBreakdown.shares || 0) }
                ]
                : [],
            total: value
        };
    }

    const descriptions = {
        [SHARES_LABEL]: 'Shares and retweets received by the data points. Also counted inside All Engagements.',
        [REACH_LABEL]: 'Estimated number of people who could have seen the mentions, based on the follower, subscriber or friend count of the publishing accounts. Potential exposure, not actual views.',
        [IMPACT_SCORE_LABEL]: 'Reflects sentiment and volume changes over time, based on positive vs total (positive + negative) data points. Smoothed, so it may not match a raw calculation.'
    };

    return { title, value, description: descriptions[label] || '' };
};

/**
 * Selector rows shared by the trend chart and the pie view.
 * `isAvailable` drops series with no data; `isDisabled` keeps a chip visible but not clickable.
 */
export const buildSeriesGroups = ({
    totals = {},
    engagementsBreakdown = null,
    isVisible,
    isAvailable = () => true,
    isDisabled = () => false,
    disabledNote = ''
}) => SERIES_GROUPS
    .map(group => ({
        title: group.title,
        series: group.labels
            .filter(isAvailable)
            .map(label => ({
                label,
                displayLabel: seriesDisplayLabel(label),
                color: SERIES_COLORS[label].border,
                isVisible: isVisible(label),
                isDefaultVisible: !DEFAULT_DISABLED_DATASETS.includes(label),
                isDisabled: isDisabled(label),
                total: formatSeriesTotal(label, totals[label]),
                tooltip: {
                    ...seriesTooltip(label, totals, engagementsBreakdown),
                    note: isDisabled(label) ? disabledNote : ''
                }
            }))
    }))
    .filter(group => group.series.length);
